// @ts-nocheck
import translate from "@insite/client-framework/Translate";
import TextField, { TextFieldProps, TextFieldPresentationProps } from "@insite/mobius/TextField";
import validateRequiredTextField from "@store/Framework/Common/Utilities/validateRequiredTextField";
import { BaseFormFieldProps, FormFieldValue } from "@store/Framework/Types/FormField";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import React, { FC, useEffect, useState } from "react";

type FieldValueType = string;
export type TextFormFieldValue = FormFieldValue<FieldValueType>;

export interface TextFormFieldPresentationProps extends ExpandStyles<TextFormFieldStyles> {}

export interface TextFormFieldProps
    extends Omit<TextFieldProps, keyof TextFieldPresentationProps | "defaultValue" | "error" | "value">,
        TextFormFieldPresentationProps,
        BaseFormFieldProps<FieldValueType> {
    prefillValue?: FieldValueType;
    requiredErrorMessage?: string;
}

export interface TextFormFieldStyles {
    textField?: TextFieldPresentationProps;
}

export const textFormFieldStyles: TextFormFieldStyles = {};

const TextFormField: FC<TextFormFieldProps> = ({
    defaultValue = "",
    extendedStyles,
    label,
    onBlur,
    onChange,
    onUpdate,
    onValidate,
    overridedStyles,
    prefillValue,
    required,
    requiredErrorMessage,
    showError = true,
    ...textFieldProps
}) => {
    const styles = useExpandProps(textFormFieldStyles, extendedStyles, overridedStyles);
    const [value, setValue] = useState(defaultValue);
    const [error, setError] = useState("");
    const textLabel = typeof label === "string" ? label : undefined;

    const validate = (value: string) => {
        let errorMessage: string | undefined;

        if (required) {
            const invalidMessage = validateRequiredTextField(value, textLabel);

            if (invalidMessage) {
                errorMessage = requiredErrorMessage || invalidMessage;
            }
        }

        errorMessage = onValidate?.({ isValid: !errorMessage, value }) ?? errorMessage;

        setError(errorMessage || "");

        return !errorMessage;
    };

    useEffect(() => {
        const isValid = validate(value);
        onUpdate?.({ isValid, value });
    }, []);

    useEffect(() => {
        if (prefillValue) {
            const newValue = value || prefillValue;

            if (newValue !== value) {
                setValue(newValue);
                const isValid = validate(newValue);
                onUpdate?.({ isValid, value: newValue });
            }
        }
    }, [prefillValue]);

    return (
        <TextField
            {...styles.textField}
            placeholder={textLabel ? translate("Enter {0}", textLabel) : undefined}
            {...textFieldProps}
            error={showError ? error : ""}
            label={label || textLabel}
            onBlur={event => {
                onBlur?.(event);
                onUpdate?.({ isValid: !error, value });
            }}
            onChange={event => {
                onChange?.(event);
                setValue(event.target.value);
                validate(event.target.value);
            }}
            required={required}
            value={value}
        />
    );
};

export default TextFormField;
